import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type ThankYouCardContent,
  type ThankYouStat,
  type ThankYouTheme,
} from "./types";

interface ThankYouCardProps {
  format: CardFormat;
  content: ThankYouCardContent;
}

const FONT_BEBAS = "'Bebas Neue', 'Anton', sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";

const FBK_GOLD = "#FFD700";

/** Resolved look for one ThankYouTheme. */
interface ThemePack {
  baseGradient: string;
  headlineColor: string;
  /** Optional background-clip gradient for the headline. */
  headlineGradient?: string;
  accent: string;
  eyebrowColor: string;
  subColor: string;
  bodyColor: string;
  statValueColor: string;
  statLabelColor: string;
  /** Border/divider colour between stat tiles. */
  ruleColor: string;
}

function getThemePack(theme: ThankYouTheme): ThemePack {
  switch (theme) {
    case "rollcall":
      return {
        baseGradient:
          "radial-gradient(ellipse 70% 50% at 50% 45%, #0d1f12 0%, #060a07 55%, #000000 100%)",
        headlineColor: "#FFFFFF",
        accent: FBK_GOLD,
        eyebrowColor: FBK_GOLD,
        subColor: "rgba(255,255,255,0.92)",
        bodyColor: "rgba(255,255,255,0.72)",
        statValueColor: FBK_GOLD,
        statLabelColor: "rgba(255,255,255,0.6)",
        ruleColor: "rgba(255,215,0,0.22)",
      };

    case "wrapped":
      return {
        // Hard colour blocking — deliberately louder than the rest of the
        // deck. This theme is meant to feel like a different product.
        baseGradient: [
          "radial-gradient(ellipse 80% 60% at 22% 18%, rgba(255,215,0,0.30) 0%, transparent 58%)",
          "radial-gradient(ellipse 70% 55% at 82% 84%, rgba(13,140,60,0.55) 0%, transparent 60%)",
          "linear-gradient(150deg, #0D8C3C 0%, #0A6E30 38%, #07321A 72%, #04170C 100%)",
        ].join(", "),
        headlineColor: "#FFFFFF",
        accent: FBK_GOLD,
        eyebrowColor: "rgba(255,255,255,0.95)",
        subColor: "#FFD700",
        bodyColor: "rgba(255,255,255,0.85)",
        statValueColor: "#FFFFFF",
        statLabelColor: "rgba(255,255,255,0.7)",
        ruleColor: "rgba(255,255,255,0.18)",
      };

    case "curtain":
      return {
        baseGradient: [
          "radial-gradient(ellipse 45% 70% at 50% 0%, rgba(255,215,0,0.20) 0%, transparent 62%)",
          "linear-gradient(180deg, #0A0A0A 0%, #050505 60%, #000000 100%)",
        ].join(", "),
        headlineColor: "#FFFFFF",
        accent: FBK_GOLD,
        eyebrowColor: "rgba(255,215,0,0.85)",
        subColor: "rgba(255,255,255,0.88)",
        bodyColor: "rgba(255,255,255,0.62)",
        statValueColor: "#FFFFFF",
        statLabelColor: "rgba(255,255,255,0.45)",
        ruleColor: "rgba(255,255,255,0.14)",
      };

    case "gratitude":
    default:
      return {
        baseGradient: [
          "radial-gradient(ellipse 90% 55% at 50% 12%, rgba(255,215,0,0.28) 0%, transparent 62%)",
          "linear-gradient(160deg, #0D8C3C 0%, #0D5C2E 45%, #07321A 78%, #051F11 100%)",
        ].join(", "),
        headlineColor: FBK_GOLD,
        headlineGradient:
          "linear-gradient(180deg, #FFFFFF 0%, #FFE9A3 38%, #FFD700 66%, #C99A00 100%)",
        accent: FBK_GOLD,
        eyebrowColor: FBK_GOLD,
        subColor: "rgba(255,255,255,0.95)",
        bodyColor: "rgba(255,255,255,0.82)",
        statValueColor: FBK_GOLD,
        statLabelColor: "rgba(255,255,255,0.7)",
        ruleColor: "rgba(255,215,0,0.25)",
      };
  }
}

const ThankYouCard = forwardRef<HTMLDivElement, ThankYouCardProps>(
  function ThankYouCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const pack = getThemePack(content.theme);
    const isWrapped = content.theme === "wrapped";

    const shellBackground = content.heroImageUrl
      ? {
          imageUrl: content.heroImageUrl,
          opacity: content.heroImageOpacity ?? 0.35,
        }
      : style.background;

    // ── Text styles (all Style-tab overridable) ──────────────────────────
    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: FONT_INTER,
          fontSizePx: 24,
          color: pack.eyebrowColor,
          letterSpacing: "0.5em",
          bold: true,
          align: "center",
        },
        format,
      ),
      textTransform: "uppercase",
      textShadow: "0 2px 10px rgba(0,0,0,0.7)",
    };

    // Wrapped puts the stats first, so its headline is deliberately smaller.
    const headlineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: FONT_BEBAS,
          fontSizePx: isWrapped ? 150 : 240,
          color: pack.headlineColor,
          letterSpacing: "0.005em",
          bold: true,
          align: "center",
        },
        format,
      ),
      lineHeight: 0.88,
      textTransform: "uppercase",
      whiteSpace: "pre-line",
      ...(pack.headlineGradient
        ? {
            background: pack.headlineGradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            filter: `drop-shadow(0 0 55px ${pack.accent}55) drop-shadow(0 8px 22px rgba(0,0,0,0.8))`,
          }
        : {
            textShadow:
              "0 6px 28px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.9)",
          }),
    };

    const subheadlineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.tagline,
        {
          fontFamily: FONT_BEBAS,
          fontSizePx: 54,
          color: pack.subColor,
          letterSpacing: "0.06em",
          bold: true,
          align: "center",
        },
        format,
      ),
      lineHeight: 1.05,
      textTransform: "uppercase",
      textShadow: "0 3px 14px rgba(0,0,0,0.75)",
    };

    const messageStyle: CSSProperties = {
      ...resolveTextStyle(
        style.body,
        {
          fontFamily: FONT_INTER,
          fontSizePx: 28,
          color: pack.bodyColor,
          bold: false,
          align: "center",
        },
        format,
      ),
      lineHeight: 1.5,
      maxWidth: isStory ? 840 : 720,
      marginLeft: "auto",
      marginRight: "auto",
      textShadow: "0 2px 10px rgba(0,0,0,0.7)",
    };

    const gap = (story: number, post: number) => (isStory ? story : post);

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("THANK_YOU")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={shellBackground}
        customBaseGradient={pack.baseGradient}
        suppressBrandOverlays
        hideFooter={content.footer?.hide}
      >
        {/* Theme backdrops */}
        {content.theme === "gratitude" && <Confetti accent={pack.accent} />}
        {content.theme === "rollcall" && (
          <NameWall names={content.names} format={format} />
        )}
        {content.theme === "curtain" && <SpotlightCone />}

        <div
          style={{
            position: "relative",
            zIndex: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 0,
          }}
        >
          {content.eyebrow && (
            <div style={{ ...eyebrowStyle, marginBottom: gap(26, 18) }}>
              {content.eyebrow}
            </div>
          )}

          {/* Wrapped leads with the numbers — they ARE the hero. Every other
              theme leads with the words and treats stats as support. */}
          {isWrapped && content.stats.length > 0 && (
            <div style={{ marginBottom: gap(36, 24), width: "100%" }}>
              <StatGrid
                stats={content.stats}
                pack={pack}
                format={format}
                hero
              />
            </div>
          )}

          <div style={headlineStyle}>{content.headline}</div>

          {content.subheadline && (
            <div style={{ ...subheadlineStyle, marginTop: gap(18, 12) }}>
              {content.subheadline}
            </div>
          )}

          {content.message && (
            <div style={{ ...messageStyle, marginTop: gap(26, 18) }}>
              {content.message}
            </div>
          )}

          {!isWrapped && content.stats.length > 0 && (
            <div style={{ marginTop: gap(44, 30), width: "100%" }}>
              <StatGrid stats={content.stats} pack={pack} format={format} />
            </div>
          )}

          {content.signature && (
            <div
              style={{
                marginTop: gap(38, 26),
                fontFamily: FONT_INTER,
                fontSize: gap(24, 18),
                fontWeight: 700,
                fontStyle: "italic",
                color: pack.subColor,
                textAlign: "center",
                textShadow: "0 2px 10px rgba(0,0,0,0.7)",
              }}
            >
              {content.signature}
            </div>
          )}
        </div>
      </FbkCardShell>
    );
  },
);

export default ThankYouCard;

// ─── Stat tiles ────────────────────────────────────────────────────────────

/**
 * Up to four stat tiles in a row, separated by thin vertical rules.
 * `hero` doubles the number size for the wrapped theme, where the stats
 * carry the card instead of supporting it.
 */
function StatGrid({
  stats,
  pack,
  format,
  hero,
}: {
  stats: ThankYouStat[];
  pack: ThemePack;
  format: CardFormat;
  hero?: boolean;
}) {
  const isStory = format === "story";
  const shown = stats.slice(0, 4);
  if (shown.length === 0) return null;

  const valueSize = hero
    ? isStory
      ? 118
      : 86
    : isStory
      ? 66
      : 48;
  const labelSize = hero ? (isStory ? 20 : 15) : isStory ? 17 : 13;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        gap: 0,
        width: "100%",
      }}
    >
      {shown.map((s, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: `0 ${isStory ? 12 : 8}px`,
            // Divider on every tile except the first
            borderLeft:
              i === 0 ? undefined : `1px solid ${pack.ruleColor}`,
          }}
        >
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: valueSize,
              lineHeight: 1,
              color: pack.statValueColor,
              letterSpacing: "0.01em",
              textAlign: "center",
              textShadow: "0 4px 18px rgba(0,0,0,0.7)",
            }}
          >
            {s.value}
          </div>
          <div
            style={{
              marginTop: isStory ? 10 : 7,
              fontFamily: FONT_INTER,
              fontSize: labelSize,
              fontWeight: 700,
              color: pack.statLabelColor,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              textAlign: "center",
              lineHeight: 1.25,
            }}
          >
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Backdrops ─────────────────────────────────────────────────────────────

/**
 * Deterministic confetti. Positions come from a fixed integer hash rather
 * than Math.random() — a re-render between the on-screen preview and the
 * html-to-image capture would otherwise reshuffle every piece, so the
 * exported PNG wouldn't match what the admin approved.
 */
function Confetti({ accent }: { accent: string }) {
  const pieces = 46;
  // Cheap deterministic PRNG — same seed always yields the same layout.
  const rand = (n: number) => {
    const x = Math.sin(n * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  };
  const palette = [accent, "#FFFFFF", "#FFE9A3", "#4ADE80"];

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: -800,
        pointerEvents: "none",
        zIndex: 1,
        overflow: "hidden",
      }}
    >
      {Array.from({ length: pieces }, (_, i) => {
        const left = rand(i + 1) * 100;
        const top = rand(i + 101) * 100;
        const rot = rand(i + 201) * 360;
        const w = 8 + rand(i + 301) * 14;
        const h = 4 + rand(i + 401) * 8;
        const color = palette[i % palette.length];
        const opacity = 0.18 + rand(i + 501) * 0.35;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: `${top}%`,
              width: w,
              height: h,
              background: color,
              opacity,
              transform: `rotate(${rot}deg)`,
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Tiled username texture for the `rollcall` theme. Names repeat to fill the
 * canvas; a radial mask darkens the centre so the headline stays readable
 * while the edges show the community.
 */
function NameWall({
  names,
  format,
}: {
  names: string[];
  format: CardFormat;
}) {
  if (names.length === 0) return null;
  const isStory = format === "story";
  const rowCount = isStory ? 30 : 22;
  const perRow = 7;
  const fontSize = isStory ? 26 : 21;

  // Repeat the list until we have enough entries to fill every slot, so a
  // small seed (say 40 usernames) still produces a dense, full-bleed wall.
  const needed = rowCount * perRow;
  const filled: string[] = [];
  for (let i = 0; i < needed; i++) {
    filled.push(names[i % names.length]);
  }

  return (
    <>
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: -800,
          pointerEvents: "none",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: isStory ? 16 : 13,
          padding: "0 40px",
        }}
      >
        {Array.from({ length: rowCount }, (_, r) => (
          <div
            key={r}
            style={{
              display: "flex",
              gap: isStory ? 30 : 24,
              whiteSpace: "nowrap",
              // Alternate rows offset slightly so the grid doesn't read as
              // a rigid table.
              transform: `translateX(${r % 2 === 0 ? -30 : 30}px)`,
            }}
          >
            {filled
              .slice(r * perRow, r * perRow + perRow)
              .map((n, c) => (
                <span
                  key={c}
                  style={{
                    fontFamily: FONT_INTER,
                    fontSize,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    opacity: 0.13,
                    letterSpacing: "0.02em",
                  }}
                >
                  {n}
                </span>
              ))}
          </div>
        ))}
      </div>
      {/* Centre scrim — pins contrast for the headline without hiding the
          names at the edges. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: -800,
          pointerEvents: "none",
          zIndex: 2,
          background:
            "radial-gradient(ellipse 62% 46% at 50% 50%, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.6) 45%, transparent 78%)",
        }}
      />
    </>
  );
}

/** Single soft spotlight cone from the top edge — the `curtain` theme. */
function SpotlightCone() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: -800,
        pointerEvents: "none",
        zIndex: 1,
        background: [
          "radial-gradient(ellipse 30% 65% at 50% 6%, rgba(255,255,255,0.10) 0%, transparent 60%)",
          "radial-gradient(ellipse 55% 40% at 50% 52%, rgba(255,215,0,0.10) 0%, transparent 70%)",
        ].join(", "),
      }}
    />
  );
}
